import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Auto light/dark theme based on local clock time.
 *
 *  06:00 – 17:59 → light
 *  18:00 – 05:59 → dark
 *
 * Users can force one or the other for the current session via `setOverride`.
 * Preference is stored in localStorage.
 *
 * Theme is applied by setting `data-theme` on <html>. An early-boot inline
 * script in `public/index.html` sets it before React mounts to avoid a flash.
 */
const KEY = "qoh_theme_override_v1"; // "light" | "dark" | null (auto)

export const themeFromHour = (h) => (h >= 6 && h < 18 ? "light" : "dark");

const readOverride = () => {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch (_) { return null; }
};

const writeOverride = (v) => {
  try {
    if (v === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, v);
  } catch (_) { /* ignore */ }
};

const apply = (t) => {
  try { document.documentElement.setAttribute("data-theme", t); } catch (_) { /* ignore */ }
};

const ThemeCtx = createContext({ theme: "dark", override: null, setOverride: () => {} });

export function ThemeProvider({ children }) {
  const [override, setOverrideState] = useState(readOverride);
  const [autoTheme, setAutoTheme] = useState(() => themeFromHour(new Date().getHours()));

  const activeTheme = override || autoTheme;

  useEffect(() => { apply(activeTheme); }, [activeTheme]);

  // Re-check the clock every 5 minutes so an operator on a 12-hour shift crosses
  // sunrise / sunset without needing to reload.
  useEffect(() => {
    const tick = () => setAutoTheme(themeFromHour(new Date().getHours()));
    tick();
    const t = setInterval(tick, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const setOverride = useCallback((v) => {
    setOverrideState(v);
    writeOverride(v);
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme: activeTheme, override, setOverride }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
